import {
  selActiveAnnotation,
  selAnnotationsPluginState
} from '@st/document-viewer-kit/plugins/annotations'
import { clsx } from 'clsx'
import { match } from '@st/pattern'
import type { FieldToolState } from '~/st-doc-viewer/annotations'
import {
  signerFieldAppearance,
  signerFieldAriaLabel,
  signerFieldShape
} from '~/st-doc-viewer/plugins/field/signer-field-appearance'
import type { ViewerFieldAnnotation } from '~/st-doc-viewer/viewer-annotations'
import { Button, TextInput } from '@st/theme'
import { useProcess, useSelect } from '~/store'
import type { ContractEditorMessage } from '~/features/contract-editing/editor'
import { selSigningSigners, selTaxReturnDeliveryPolicy } from './state'

// Initial signers come from draft creation; later folder changes do not sync
// them. Names and boxes save together with the sidebar's Save changes.
export function TaxReturnDeliveryFieldEditor() {
  const folder = useProcess('stFolder')
  const sendContract = (message: ContractEditorMessage) =>
    folder.send({ type: 'taxReturnDelivery', message: { type: 'contract', message } })
  const { addableRoles, removableSignerIds, canEditFields } = useSelect(
    'stFolder',
    selTaxReturnDeliveryPolicy
  )
  const signers = useSelect('stFolder', selSigningSigners)
  const viewer = useSelect('stFolder', (state) => state.viewer)
  const annotationState = selAnnotationsPluginState(viewer)
  const selectedAnnotation = selActiveAnnotation(viewer)
  const activeField = match(selectedAnnotation)
    .with({ type: 'field' }, (annotation) => annotation as ViewerFieldAnnotation)
    .otherwise(() => undefined)
  const fieldTool = annotationState.toolStates.field as FieldToolState

  return (
    <fieldset disabled={!canEditFields} className="flex flex-col gap-4 px-3 py-3">
      <p className="text-xs leading-relaxed text-stone-500">
        Select a field and click on the document to place it
      </p>
      {signers.map((signer) => (
        <section key={signer.id} className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-medium uppercase tracking-widest text-gray-500">
              {signer.role}
            </h3>
            {removableSignerIds.includes(signer.id) && (
              <Button
                variant="link"
                className="py-0 text-sm"
                onClick={() => sendContract({ type: 'signerRemoveRequested', signerId: signer.id })}
              >
                Remove spouse
              </Button>
            )}
          </div>
          <TextInput
            ariaLabel={`${signer.role} name`}
            placeholder="Full name"
            autoComplete="off"
            value={signer.displayName ?? ''}
            onChange={(displayName) =>
              sendContract({ type: 'signerRenamed', signerId: signer.id, displayName })
            }
          />
          {(['signature', 'date'] as const).map((fieldType) => (
            <FieldButton
              key={fieldType}
              signerRole={signer.role}
              fieldType={fieldType}
              selected={isSelectedPlacement(fieldTool, activeField, signer.id, fieldType)}
              onClick={() =>
                sendContract({ type: 'fieldPlacementRequested', signerId: signer.id, fieldType })
              }
            />
          ))}
        </section>
      ))}
      {addableRoles.includes('spouse') && (
        <Button
          variant="link"
          className="self-start text-sm"
          onClick={() => sendContract({ type: 'signerAddRequested', role: 'spouse' })}
        >
          + Add spouse
        </Button>
      )}
    </fieldset>
  )
}

function isSelectedPlacement(
  tool: FieldToolState,
  activeAnnotation: ViewerFieldAnnotation | undefined,
  signerId: string,
  fieldType: 'signature' | 'date'
) {
  return (
    !activeAnnotation &&
    tool.placement?.field.signerId == signerId &&
    tool.placement.field.type == fieldType
  )
}

function FieldButton({
  signerRole,
  fieldType,
  selected,
  onClick
}: {
  signerRole: string
  fieldType: 'signature' | 'date'
  selected: boolean
  onClick: () => void
}) {
  const appearance = signerFieldAppearance(signerRole)

  return (
    <button
      type="button"
      aria-label={signerFieldAriaLabel(signerRole, fieldType)}
      className={clsx(
        appearance.field,
        appearance.hover,
        signerFieldShape,
        'h-8 w-full',
        selected && `ring-1 ring-offset-1 ${appearance.selected}`
      )}
      onClick={onClick}
    >
      {signerFieldAriaLabel(signerRole, fieldType)}
    </button>
  )
}
